import {useMutation, useQuery, useQueryClient} from "react-query";
import axios from "axios";
import {PagedSearchParams} from "@atiautomacao/ati-ui-library";
import {useSnackbar} from "notistack";
import {useAppSelector} from "../../../../Config/Hooks";
import {RootState} from "../../../../Config/Store";
import {UnavailabilityPowerStation} from "../../../../Config/Types";

interface UnavailabilityPowerStationResponse {
    message: string;
    data: UnavailabilityPowerStation[]
}

const apiUrl = "api/unavailability";
const apiUrlPowerStation = "api/power-station";

const saveUnavailability = async (data: any) => {
    console.log("dentro de saveUnavailability", data);
    try {
        if (data.id != null) {
            return await axios.put(`${apiUrl}/${data.id}`, data, {timeout: 30000}).then((res) => res.data);
        } else {
            return await axios.post(apiUrl, data, {timeout: 30000}).then((res) => res.data);
        }
    } catch (error) {
        console.error("Error saving unavailability:", error);
        return Promise.reject(error);
    }
};

export const SaveUnavailabilityReactQuery = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: (data: any) => {
            return saveUnavailability(data);
        },
        onSuccess: () => {
            closeSnackbar()
            enqueueSnackbar("Indisponibilidade salva com sucesso!", { variant: "success" });
        },
        onError: (error: any) => {
            closeSnackbar()
            if (error?.response?.status === 409 && error?.response?.data?.title?.includes("Already exists")) {
                enqueueSnackbar(error?.response?.data?.detail, { variant: "error", autoHideDuration: 1500 });
                return
            }
            if (error?.response?.status === 503) {
                enqueueSnackbar("Serviço indisponível", { variant: "error", autoHideDuration: 1500 });
                return
            }
            const errorMessages = error?.response?.data?.detail?.split('/').filter((s: any) => s.trim() !== '') ?? ["Erro desconhecido. Informe ao suporte."]
            const result = errorMessages.map((erro: any) => {
                return erro.replace(/^.*?:\s*/, "")
            })

            result.forEach((message: any) => {
                enqueueSnackbar(`${message}`, { variant: "error", autoHideDuration: 2000 });
            })

        }
    })
};

const finalizeUnavailabilities = async (payload: { idList: number[]; endDateTime?: string | null }) => {
    try {
        const url = `${apiUrl}/finalize`;
        const response = await axios.put(url, payload, { timeout: 30000 });
        return response.data;
    } catch (error) {
        console.error("Error finalizing unavailabilities:", error);
        return Promise.reject(error);
    }
};

export const FinalizeUnavailabilityReactQuery = (pagedSearchParams?: PagedSearchParams) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (payload: { idList: number[]; endDateTime?: string | null }) => finalizeUnavailabilities(payload),
        onSuccess: () => {
            closeSnackbar();
            enqueueSnackbar("Indisponibilidades finalizadas com sucesso!", { variant: "success" });
            if (pagedSearchParams) {
                queryClient.invalidateQueries(["data"]);
            }
            queryClient.invalidateQueries('find-all-unavailabilities');
        },
        onError: (error: any) => {
            closeSnackbar();
            enqueueSnackbar(`Erro ao finalizar indisponibilidades: ${error?.message ?? 'Erro desconhecido'}`, { variant: "error" });
        }
    });
};

const fetchUnavailability = async (pagedSearchParams: PagedSearchParams, rangeStart?: string | null, rangeEnd?: string | null) => {
    try {
        const params = pagedSearchParams ? pagedSearchParams.toURLSearchParams() : new URLSearchParams();
        if (rangeStart) {
            params.append("rangeStart", rangeStart);
        }
        if (rangeEnd) {
            params.append("rangeEnd", rangeEnd);
        }
        return await axios
            .get(`${apiUrl}/search`, {
                params,
                timeout: 30000
            })
            .then((res) => res.data);
    } catch (error) {
        console.error("Error fetching unavailability:", error);
        return Promise.reject(error);
    }
};

export const UnavailabilityReactQuery = (pagedSearchParams: PagedSearchParams, rangeStart?: string | null, rangeEnd?: string | null) => {
    const userConfig = useAppSelector((state: RootState) => state.userConfigurarion);
    return useQuery({
        queryKey: ["data", pagedSearchParams, rangeStart, rangeEnd],
        queryFn: () => {
            return fetchUnavailability(pagedSearchParams, rangeStart, rangeEnd);
        },
        refetchInterval: userConfig.unavailabilityUpdateInterval,
        refetchOnWindowFocus: false,
    });
};

const fetchUnavailabilityById = async (id: number) => {
    try {
        return await axios.get(`${apiUrl}/${id}`, {timeout: 30000}).then((res) => res.data);
    } catch (error) {
        console.error("Error fetching unavailability by id:", error);
        return Promise.reject(error);
    }
};

export const FindUnavailabilityByIdReactQuery = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    return useMutation({
        mutationFn: (id: number) => {
            return fetchUnavailabilityById(id);
        },
        onError: (error: any) => {
            closeSnackbar()
            enqueueSnackbar(`Erro ao carregar uma indisponibilidade: ${error.message}`, { variant: "error" });
        }
    })
};

const deleteUnavailability = async (id: number) => {
    try {
        return await axios.delete(`${apiUrl}/${id}`, {timeout: 30000}).then((res) => res.data);
    } catch (error) {
        console.error("Error deleting unavailability:", error);
        return Promise.reject(error);
    }
};

export const DeleteUnavailabilityReactQuery = (pagedSearchParams: PagedSearchParams) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (data: any) => {
            return deleteUnavailability(data.id);
        },
        onSuccess: () => {
            closeSnackbar()
            enqueueSnackbar("Indisponibilidade excluída com sucesso!", { variant: "success" });
            if (pagedSearchParams) {
                queryClient.invalidateQueries(["data"]);
            }
        },
        onError: (error: any) => {
            closeSnackbar()
            enqueueSnackbar(`Erro ao excluir indisponibilidade: ${error.message}`, { variant: "error" });
        }
    })
};

const fetchUnavailabilities = async () => {
    try {
        const url = `${apiUrl}/find-all`;
        const response = await axios.get(url);
        return response.data;
    } catch (error) {
        console.error("Error fetching all unavailabilities:", error);
        throw error;
    }
};

export const FindAllUnavailabilityReactQuery = () => {
    return useQuery(
        'find-all-unavailabilities',
        fetchUnavailabilities,
    );
};

const fetchPowerStationAndEquipmentNames = async () => {
    try {
        const url = `${apiUrl}/power-station-and-equipment-names`;
        const response = await axios.get(url, { timeout: 30000 });
        return response.data;
    } catch (error) {
        console.error("Error fetching power station and equipment names:", error);
        return Promise.reject(error);
    }
};

export const FindPowerStationAndEquipmentNamesReactQuery = () => {
    return useQuery(
        'power-station-and-equipment-names',
        fetchPowerStationAndEquipmentNames,
    );
};

const fetchPowerStationsWithTeleobjectAlarmCondition = async () => {
    try {
        const url = `${apiUrlPowerStation}/find-all-with-teleobject-alarm-condition`;
        const response = await axios.get(url, { timeout: 30000 });
        return response.data;
    } catch (error) {
        console.error("Error fetching power stations with teleobject alarm condition:", error);
        return Promise.reject(error);
    }
};

export const FindAllWithTeleobjectAlarmConditionReactQuery = () => {
    return useQuery(
        'find-all-with-teleobject-alarm-condition',
        fetchPowerStationsWithTeleobjectAlarmCondition,
    );
};

const deleteAllUnavailabilities = async (ids: number[]) => {
    try {
        const url = `${apiUrl}/delete-all`;
        const response = await axios.post(url, ids, { timeout: 30000 });
        return response.data;
    } catch (error) {
        console.error("Error deleting all unavailabilities:", error);
        return Promise.reject(error);
    }
};

const exportUnavailability = async (
    format: "csv" | "excel",
    pagedSearchParams: PagedSearchParams,
    rangeStart?: string | null,
    rangeEnd?: string | null
) => {
    const params = pagedSearchParams ? pagedSearchParams.toURLSearchParams() : new URLSearchParams();
    if (rangeStart) {
        params.append("rangeStart", rangeStart);
    }
    if (rangeEnd) {
        params.append("rangeEnd", rangeEnd);
    }
    const response = await axios.post(`${apiUrl}/export/${format}`, null, {
        params,
        responseType: "blob",
        timeout: 60000,
    });

    const contentDisposition = response.headers["content-disposition"] as string | undefined;
    let filename = format === "csv" ? "unavailability.csv" : "unavailability.xlsx";
    if (contentDisposition) {
        const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(contentDisposition);
        if (match?.[1]) {
            filename = decodeURIComponent(match[1]);
        }
    }

    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const ExportUnavailabilityReactQuery = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: (payload: {
            format: "csv" | "excel";
            pagedSearchParams: PagedSearchParams;
            rangeStart?: string | null;
            rangeEnd?: string | null;
        }) => exportUnavailability(payload.format, payload.pagedSearchParams, payload.rangeStart, payload.rangeEnd),
        onSuccess: () => {
            closeSnackbar();
            enqueueSnackbar("Exportação concluída com sucesso!", { variant: "success" });
        },
        onError: (error: any) => {
            closeSnackbar();
            enqueueSnackbar(`Erro ao exportar indisponibilidades: ${error?.message ?? "Erro desconhecido"}`, { variant: "error" });
        }
    });
};

export const DeleteAllUnavailabilityReactQuery = (pagedSearchParams?: PagedSearchParams) => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (ids: number[]) => deleteAllUnavailabilities(ids),
        onSuccess: () => {
            closeSnackbar();
            enqueueSnackbar("Indisponibilidades excluídas com sucesso!", { variant: "success" });
            // Invalida a lista paginada se estiver sendo usada e a lista geral
            if (pagedSearchParams) {
                queryClient.invalidateQueries(["data"]);
            }
            queryClient.invalidateQueries('find-all-unavailabilities');
        },
        onError: (error: any) => {
            closeSnackbar();
            enqueueSnackbar(`Erro ao excluir indisponibilidades: ${error?.message ?? 'Erro desconhecido'}`, { variant: "error" });
        }
    })
};

const getUnavailabilityPowerStations = async (startDateTime: string , endDateTime: string): Promise<Array<UnavailabilityPowerStationResponse>> => {
    try {
        return await axios.get(`${apiUrl}/get-powerstations-unavailability?startDateTime=${startDateTime}&endDateTime=${endDateTime}`, {timeout: 30000}).then((res) => res.data.data);
    } catch (error) {
        console.error("Error fetch unavailability:", error);
        throw (error);
    }
};

export const FindUnavailabilityPowerStationsReactQuery = (startDateTime: string | null, endDateTime: string | null) => {
    const start = startDateTime || "";
    const end = endDateTime || "";
    return useQuery({
        queryKey: ['unavailability-power-stations', start, end],
        queryFn: () => getUnavailabilityPowerStations(start, end),
        enabled: !!start && !!end,
        staleTime: 1000 * 60 * 2,
        placeholderData: (previousData:any) => previousData,
    });
};

const exportDinDexLosses = async (
    format: "csv" | "excel",
    startDateTime: string,
    endDateTime: string
) => {
    const response = await axios.get(`${apiUrl}/power-station/export/${format}`, {
        params: { startDateTime, endDateTime },
        responseType: "blob",
        timeout: 60000,
    });

    const contentDisposition = response.headers["content-disposition"] as string | undefined;
    const extension = format === "csv" ? "csv" : "xlsx";
    const now = new Date();
    const dd = String(now.getDate()).padStart(2, '0');
    const mm = String(now.getMonth() + 1).padStart(2, '0');
    const yyyy = now.getFullYear();
    let filename = `perdas_indisponibilidade_dindex_${dd}_${mm}_${yyyy}.${extension}`;
    if (contentDisposition) {
        const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)/i.exec(contentDisposition);
        if (match?.[1]) {
            filename = decodeURIComponent(match[1]);
        }
    }

    const blob = new Blob([response.data]);
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
};

export const ExportDinDexLossesReactQuery = () => {
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    return useMutation({
        mutationFn: (payload: {
            format: "csv" | "excel";
            startDateTime: string;
            endDateTime: string;
        }) => exportDinDexLosses(payload.format, payload.startDateTime, payload.endDateTime),
        onSuccess: () => {
            closeSnackbar();
            enqueueSnackbar("Exportação concluída com sucesso!", { variant: "success", autoHideDuration: 1500 });
        },
        onError: (error: any) => {
            closeSnackbar();
            enqueueSnackbar("Erro ao exportar perdas DIN/DEX", { variant: "error" });
        }
    });
};
